import { useId } from 'react';

interface IconProps {
  className?: string;
}

// POLYGON brand icon — three groundwater waves with an iridescent middle
// wave (oil-on-water gradient) and a white site polygon + well markers
// layered on top for a multi-dimensional read. Shares the wave geometry
// with PhreaticIcon.
//
// One component, two renders:
//  - brand: full color. Used at hero / card sizes (h-7 and up).
//  - mono : currentColor strokes only. Used in the nav and other small
//           contexts (h-6 and down) where the gradient would muddy.
const WAVE_TOP = 'M2 6.6c2.5-2.7 7.5-2.7 10 0s7.5 2.7 10 0';
const WAVE_MID = 'M2 12c2.5-2.7 7.5-2.7 10 0s7.5 2.7 10 0';
const WAVE_BOT = 'M2 17.4c2.5-2.7 7.5-2.7 10 0s7.5 2.7 10 0';
// Site polygon — a delineation triangle with a well marker at each vertex.
const POLYGON = 'M12 4.3 19 16.4 5.4 18.2Z';
const WELLS: ReadonlyArray<readonly [number, number]> = [
  [12, 4.3],
  [19, 16.4],
  [5.4, 18.2],
];

export function PolygonIcon({ className = 'h-6 w-6' }: IconProps) {
  // useId() can contain ':' — strip it so the gradient ref stays a valid url().
  const gradId = `polygon-iris-${useId().replace(/:/g, '')}`;
  // Small contexts get the flat, tintable version.
  const mono = /h-[0-6](?![0-9])/.test(className);

  if (mono) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <path d={WAVE_TOP} />
        <path d={WAVE_MID} />
        <path d={WAVE_BOT} />
        <path d={POLYGON} />
        {WELLS.map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={1.3} fill="currentColor" stroke="none" />
        ))}
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="24" y2="6" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="22%" stopColor="#3b82f6" />
          <stop offset="44%" stopColor="#a855f7" />
          <stop offset="64%" stopColor="#ec4899" />
          <stop offset="82%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#34d399" />
        </linearGradient>
      </defs>

      {/* Cyan outer waves */}
      <path d={WAVE_TOP} stroke="#22d3ee" strokeWidth={1.6} />
      <path d={WAVE_BOT} stroke="#22d3ee" strokeWidth={1.6} />

      {/* Iridescent middle wave — soft bloom under a crisp stroke */}
      <path d={WAVE_MID} stroke={`url(#${gradId})`} strokeWidth={5} opacity={0.3} />
      <path d={WAVE_MID} stroke={`url(#${gradId})`} strokeWidth={2.6} />

      {/* White site polygon + well markers, layered on top */}
      <path d={POLYGON} stroke="#ffffff" strokeWidth={1.6} fill="#ffffff" fillOpacity={0.08} />
      {WELLS.map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={1.5} fill="#ffffff" />
      ))}
    </svg>
  );
}

export { PolygonIcon as default };
